import React, { useRef, useEffect, useState, useCallback } from 'react';
import { fabric } from 'fabric';
import { saveAs } from 'file-saver';
import { UploadedImage, ImageTransform } from '../types';

interface CanvasEditorProps {
    uploadedImage: UploadedImage | null;
}

const CanvasEditor: React.FC<CanvasEditorProps> = ({ uploadedImage }) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const fabricCanvasRef = useRef<fabric.Canvas | null>(null);
    const [frameImage, setFrameImage] = useState<fabric.Image | null>(null);
    const [userImageObject, setUserImageObject] = useState<fabric.Image | null>(null);
    const [imageTransform, setImageTransform] = useState<ImageTransform>({
        x: 0,
        y: 0,
        scale: 1,
        rotation: 0
    });
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [canvasReady, setCanvasReady] = useState(false);
    const loadingRef = useRef(false);
    const prevDisplayScaleRef = useRef<number>(1);
    const frameNaturalSizeRef = useRef<{ iw: number; ih: number } | null>(null);
    const resizeObserverRef = useRef<ResizeObserver | null>(null);
    const resizeRafIdRef = useRef<number | null>(null);

    const computeDisplayFromContainer = useCallback(() => {
        const rect = containerRef.current?.getBoundingClientRect();
        const viewportW = window.innerWidth;
        const viewportH = window.innerHeight;
        const isMobile = viewportW <= 768;
        const availableW = Math.max(320, Math.floor((rect?.width || viewportW) - 0));
        const availableH = isMobile
            ? Math.max(280, Math.floor(viewportH * 0.7))
            : Math.max(300, Math.floor(rect?.height || viewportH * 0.7));
        const { iw, ih } = frameNaturalSizeRef.current || { iw: 800, ih: 800 };
        const isRoughlySquare = Math.abs(iw / ih - 1) < 0.05;

        let displayW: number;
        let displayH: number;
        let scale: number;
        if (isRoughlySquare) {
            const side = Math.min(availableW, availableH);
            displayW = side;
            displayH = side;
            scale = side / iw;
        } else {
            scale = Math.min(availableW / iw, availableH / ih);
            displayW = Math.round(iw * scale);
            displayH = Math.round(ih * scale);
        }
        return { displayW, displayH, scale };
    }, []);

    const setCanvasSize = useCallback((canvas: fabric.Canvas, displayW: number, displayH: number) => {
        const ratio = Math.max(window.devicePixelRatio || 1, 1);
        (canvas as any).setDimensions({ width: displayW * ratio, height: displayH * ratio }, { backstoreOnly: true });
        (canvas as any).setDimensions({ width: displayW, height: displayH });
        canvas.calcOffset();
    }, []);

    useEffect(() => {
        if (canvasRef.current && !fabricCanvasRef.current) {
            const canvas = new fabric.Canvas(canvasRef.current, {
                backgroundColor: '#ffffff',
                selection: false,
                preserveObjectStacking: true,
                enableRetinaScaling: true,
            });
            (canvas as any).controlsAboveOverlay = true;

            fabricCanvasRef.current = canvas;
            setCanvasReady(true);

            return () => {
                canvas.dispose();
                fabricCanvasRef.current = null;
            };
        }
    }, []);

    useEffect(() => {
        if (!fabricCanvasRef.current || !canvasReady) return;

        setIsLoading(true);
        loadingRef.current = true;
        setError(null);

        const base = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
        const frameCandidates = [
            `${base}/assets/frame.png`,
            `${base}/frame.png`,
            '/assets/frame.png',
            '/frame.png'
        ];
        let cancelled = false;

        const attemptLoad = (url: string, idx: number) => {
            if (cancelled) return;
            const testImg = new Image();
            testImg.crossOrigin = 'anonymous';
            testImg.onload = () => {
                if (cancelled || !fabricCanvasRef.current) return;
                const iw = testImg.naturalWidth || (testImg as any).width || 800;
                const ih = testImg.naturalHeight || (testImg as any).height || 800;
                frameNaturalSizeRef.current = { iw, ih };

                const { displayW, displayH, scale } = computeDisplayFromContainer();
                const canvas = fabricCanvasRef.current!;
                setCanvasSize(canvas, displayW, displayH);
                prevDisplayScaleRef.current = scale || 1;

                canvas.setOverlayImage(url, () => {
                    if (cancelled) return;
                    const overlay = canvas.overlayImage as fabric.Image | null;
                    if (overlay) {
                        overlay.set({ left: 0, top: 0, scaleX: scale, scaleY: scale, selectable: false, evented: false });
                        setFrameImage(overlay);
                    }
                    setIsLoading(false);
                    loadingRef.current = false;
                    canvas.renderAll();
                }, {
                    crossOrigin: 'anonymous',
                    left: 0,
                    top: 0,
                    scaleX: scale,
                    scaleY: scale,
                    selectable: false,
                    evented: false
                } as any);
            };
            testImg.onerror = () => {
                if (cancelled) return;
                const nextIdx = idx + 1;
                if (nextIdx < frameCandidates.length) {
                    attemptLoad(frameCandidates[nextIdx], nextIdx);
                } else {
                    createSimpleFrame();
                }
            };
            testImg.src = url;
        };

        attemptLoad(frameCandidates[0], 0);

        return () => { cancelled = true; };
    }, [canvasReady, computeDisplayFromContainer, setCanvasSize]);

    const createSimpleFrame = () => {
        const iw = 800;
        const ih = 800;
        frameNaturalSizeRef.current = { iw, ih };

        const off = document.createElement('canvas');
        off.width = iw;
        off.height = ih;
        const ctx = off.getContext('2d')!;
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, iw, ih);
        ctx.strokeStyle = '#667eea';
        ctx.lineWidth = 8;
        ctx.strokeRect(20, 20, iw - 40, ih - 40);
        ctx.fillStyle = '#667eea';
        ctx.font = 'bold 28px Arial, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('Profile Frame', iw / 2, ih / 2);

        const canvas = fabricCanvasRef.current;
        if (!canvas) return;
        const { displayW, displayH, scale } = computeDisplayFromContainer();
        setCanvasSize(canvas, displayW, displayH);
        prevDisplayScaleRef.current = scale || 1;

        fabric.Image.fromURL(off.toDataURL(), (img) => {
            if (!img || !fabricCanvasRef.current) return;
            img.set({ left: 0, top: 0, scaleX: scale, scaleY: scale, selectable: false, evented: false });
            fabricCanvasRef.current.setOverlayImage(img, fabricCanvasRef.current.renderAll.bind(fabricCanvasRef.current));
            setFrameImage(img);
            setIsLoading(false);
            loadingRef.current = false;
        });
    };

    useEffect(() => {
        if (fabricCanvasRef.current && frameImage) {
            const canvas = fabricCanvasRef.current;
            canvas.setOverlayImage(frameImage, canvas.renderAll.bind(canvas));
        }
    }, [frameImage]);

    useEffect(() => {
        const handleResize = () => {
            const canvas = fabricCanvasRef.current;
            const overlay = frameImage as fabric.Image | null;
            const sizeInfo = frameNaturalSizeRef.current;
            if (!canvas || !overlay || !sizeInfo) return;

            const { displayW, displayH, scale: newScale } = computeDisplayFromContainer();
            const prevScale = prevDisplayScaleRef.current || 1;
            const factor = newScale / prevScale;

            setCanvasSize(canvas, displayW, displayH);

            overlay.set({ left: 0, top: 0, scaleX: newScale, scaleY: newScale, selectable: false, evented: false });
            canvas.setOverlayImage(overlay, canvas.renderAll.bind(canvas));

            canvas.getObjects().forEach((obj) => {
                obj.set({
                    left: (obj.left || 0) * factor,
                    top: (obj.top || 0) * factor,
                    scaleX: (obj.scaleX || 1) * factor,
                    scaleY: (obj.scaleY || 1) * factor,
                });
                obj.setCoords();
            });

            if (userImageObject) {
                setImageTransform(prev => ({
                    x: (userImageObject.left || 0),
                    y: (userImageObject.top || 0),
                    scale: (userImageObject.scaleX || 1),
                    rotation: (userImageObject.angle || 0)
                }));
            }

            prevDisplayScaleRef.current = newScale;
            canvas.renderAll();
        };

        const scheduleResize = () => {
            if (resizeRafIdRef.current !== null) cancelAnimationFrame(resizeRafIdRef.current);
            resizeRafIdRef.current = requestAnimationFrame(() => {
                resizeRafIdRef.current = null;
                handleResize();
            });
        };

        if ('ResizeObserver' in window && containerRef.current) {
            const ro = new ResizeObserver(() => scheduleResize());
            ro.observe(containerRef.current);
            resizeObserverRef.current = ro;
        }
        window.addEventListener('resize', scheduleResize);
        window.addEventListener('orientationchange', scheduleResize);
        return () => {
            window.removeEventListener('resize', scheduleResize);
            window.removeEventListener('orientationchange', scheduleResize);
            resizeObserverRef.current?.disconnect();
            if (resizeRafIdRef.current !== null) cancelAnimationFrame(resizeRafIdRef.current);
        };
    }, [frameImage, userImageObject, computeDisplayFromContainer, setCanvasSize]);

    useEffect(() => {
        if (uploadedImage && fabricCanvasRef.current && frameImage) {
            const canvas = fabricCanvasRef.current;
            fabric.Image.fromURL(uploadedImage.previewUrl, (img) => {
                if (userImageObject) canvas.remove(userImageObject);

                const cw = canvas.getWidth();
                const ch = canvas.getHeight();
                const imageWidth = img.width || 1;
                const imageHeight = img.height || 1;
                const padding = Math.min(cw, ch) * 0.08;
                const maxWidth = cw - padding * 2;
                const maxHeight = ch - padding * 2;
                const scaleX = maxWidth / imageWidth;
                const scaleY = maxHeight / imageHeight;
                const scale = Math.min(scaleX, scaleY, 1);

                img.set({
                    scaleX: scale,
                    scaleY: scale,
                    left: (cw - imageWidth * scale) / 2,
                    top: (ch - imageHeight * scale) / 2,
                    selectable: true,
                    evented: true,
                    hasControls: true,
                    hasBorders: true,
                    cornerStyle: 'circle',
                    cornerColor: '#667eea',
                    cornerSize: (window.innerWidth <= 768 ? 18 : 12),
                    transparentCorners: false,
                    borderColor: '#667eea',
                    borderDashArray: [5, 5],
                    rotatingPointOffset: (window.innerWidth <= 768 ? 40 : 30),
                    lockUniScaling: true
                });

                canvas.add(img);
                setUserImageObject(img);
                canvas.setActiveObject(img);
                canvas.renderAll();

                setImageTransform({
                    x: img.left || 0,
                    y: img.top || 0,
                    scale: scale,
                    rotation: img.angle || 0
                });

                img.on('moving', () => {
                    setImageTransform(prev => ({ ...prev, x: img.left || 0, y: img.top || 0 }));
                });
                img.on('rotating', () => {
                    setImageTransform(prev => ({ ...prev, rotation: img.angle || 0 }));
                });
                img.on('scaling', () => {
                    setImageTransform(prev => ({ ...prev, scale: img.scaleX || 1 }));
                });
            });
        }
    }, [uploadedImage, frameImage]);

    const handleScaleChange = useCallback((newScale: number) => {
        if (userImageObject && fabricCanvasRef.current) {
            userImageObject.set({ scaleX: newScale, scaleY: newScale });
            userImageObject.setCoords();
            fabricCanvasRef.current.renderAll();
            setImageTransform(prev => ({ ...prev, scale: newScale }));
        }
    }, [userImageObject]);

    const handleRotationChange = useCallback((rotation: number) => {
        if (userImageObject && fabricCanvasRef.current) {
            userImageObject.set({ angle: rotation });
            userImageObject.setCoords();
            fabricCanvasRef.current.renderAll();
            setImageTransform(prev => ({ ...prev, rotation }));
        }
    }, [userImageObject]);

    const handleReset = useCallback(() => {
        if (userImageObject && fabricCanvasRef.current) {
            const cw = fabricCanvasRef.current.getWidth();
            const ch = fabricCanvasRef.current.getHeight();
            const imageWidth = userImageObject.width || 1;
            const imageHeight = userImageObject.height || 1;
            const padding = Math.min(cw, ch) * 0.08;
            const maxWidth = cw - padding * 2;
            const maxHeight = ch - padding * 2;
            const scaleX = maxWidth / imageWidth;
            const scaleY = maxHeight / imageHeight;
            const scale = Math.min(scaleX, scaleY, 1);

            userImageObject.set({
                scaleX: scale,
                scaleY: scale,
                angle: 0,
                left: (cw - imageWidth * scale) / 2,
                top: (ch - imageHeight * scale) / 2,
            });
            userImageObject.setCoords();
            fabricCanvasRef.current.renderAll();
            setImageTransform({ x: userImageObject.left || 0, y: userImageObject.top || 0, scale, rotation: 0 });
        }
    }, [userImageObject]);

    const handleDownload = useCallback(() => {
        const canvas = fabricCanvasRef.current;
        if (!canvas) return;
        const overlay = frameImage;
        const naturalW = overlay?.width || canvas.getWidth();
        const multiplier = naturalW / canvas.getWidth();
        const dataURL = canvas.toDataURL({ format: 'png', quality: 1, multiplier: multiplier });
        fetch(dataURL).then(r => r.blob()).then(b => saveAs(b, `social-media-profile-${Date.now()}.png`));
    }, [frameImage]);

    if (error) {
        return (
            <div className="canvas-container">
                <div className="canvas-editor" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '300px' }}>
                    <div style={{ textAlign: 'center', color: '#ff4444' }}>
                        <div style={{ fontSize: '2rem', marginBottom: '10px' }}>⚠️</div>
                        <p>{error}</p>
                        <button onClick={() => window.location.reload()} style={{ background: '#667eea', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '4px', cursor: 'pointer' }}>Refresh Page</button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div>
            <div className="canvas-container">
                {isLoading && (
                    <div className="canvas-overlay">
                        <div className="canvas-loading">
                            <div style={{ fontSize: '2rem', marginBottom: '10px' }}>🎨</div>
                            <h3>Loading your custom frame...</h3>
                        </div>
                    </div>
                )}
                <div className="canvas-editor" ref={containerRef}>
                    <canvas ref={canvasRef} />
                </div>
            </div>

            {uploadedImage && userImageObject && (
                <div className="controls">
                    <div className="control-group">
                        <label className="control-label">Scale: {imageTransform.scale.toFixed(2)}x</label>
                        <input type="range" min="0.1" max="3" step="0.1" value={imageTransform.scale} onChange={(e) => handleScaleChange(parseFloat(e.target.value))} className="control-slider" />
                    </div>

                    <div className="control-group">
                        <label className="control-label">Rotation: {imageTransform.rotation.toFixed(0)}°</label>
                        <input type="range" min="-180" max="180" step="1" value={imageTransform.rotation} onChange={(e) => handleRotationChange(parseInt(e.target.value))} className="control-slider" />
                    </div>

                    <div className="control-group">
                        <label className="control-label">Position</label>
                        <p style={{ fontSize: '0.9rem', color: '#666' }}>Drag the image to reposition it within the frame</p>
                    </div>

                    <div className="control-buttons">
                        <button onClick={handleReset} className="control-btn">🔄 Reset Position</button>
                        <button onClick={handleDownload} className="download-btn">💾 Download High Quality Image</button>
                    </div>
                </div>
            )}

            {!uploadedImage && (
                <div className="canvas-editor" style={{ textAlign: 'center', color: '#666', padding: '60px 20px' }}>
                    <div style={{ fontSize: '4rem', marginBottom: '20px' }}>📷</div>
                    <h3 style={{ marginBottom: '12px', color: '#333' }}>Upload an image to get started</h3>
                    <p style={{ fontSize: '1.1rem', marginBottom: '8px' }}>Your image will appear here with your custom frame overlay</p>
                    <p style={{ fontSize: '0.9rem', color: '#999' }}>You can then adjust position, scale, and rotation before downloading</p>
                </div>
            )}
        </div>
    );
};

export default CanvasEditor;
